/**
 * Maps table metres to canvas pixels (TBL-001, extended in UI-003).
 *
 * The table is drawn in whichever orientation makes it BIGGER in the space available:
 * rotated, with its long axis down the screen, on a portrait phone (PRD D-005); and
 * the natural way round, long axis across, on a landscape window. Both fall out of one
 * comparison, so there is no breakpoint to keep in step with the CSS.
 *
 * The sim is entirely SI and the renderer is entirely pixels. This is the only place
 * the two meet — so if a distance looks wrong on screen, it is wrong here, not in the
 * physics.
 *
 * ⚠️ Everything screen-facing that depends on the orientation is DERIVED from the
 * mapping below, never written out a second time: the table's drawn box, the aim
 * bearing, and the sense of rotation. Rotating the table flips handedness, and a
 * hand-written constant that disagreed would send the fine-adjust buttons the wrong
 * way (CLAUDE.md L28). Deriving it makes that disagreement impossible rather than
 * merely tested for.
 */
import type { TableSpec } from '@potted/sim';

export type Orientation =
  /** Long axis down the screen — the table is rotated. A phone held upright. */
  | 'portrait'
  /** Long axis across the screen — the table as you would stand at it. */
  | 'landscape';

/** What the player asked for in Settings: a fixed way round, or whichever is bigger. */
export type OrientationPreference = Orientation | 'auto';

export interface ViewportOptions {
  /** Default `'auto'`: the comparison below decides. */
  readonly orientation?: OrientationPreference;
  /** Fraction of the largest fitting size to draw at, 0–1. Default 1. */
  readonly fill?: number;
}

export interface ScreenVector {
  readonly x: number;
  readonly y: number;
}

export interface TableRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface Viewport {
  /** Canvas size in CSS pixels. */
  readonly cssWidth: number;
  readonly cssHeight: number;
  /** Canvas pixels per CSS pixel, from `resolution.ts` (no longer capped at 2). */
  readonly dpr: number;
  /** Pixels per metre. Uniform: the table is never stretched. */
  readonly scale: number;
  readonly orientation: Orientation;
  /** Where the table's origin lands on screen, in CSS pixels. */
  readonly originX: number;
  readonly originY: number;
  /**
   * +1 if a positive (counter-clockwise) rotation in TABLE coordinates also reads
   * counter-clockwise on screen, −1 if the orientation flips it. Measured from the
   * mapping, not declared.
   */
  readonly screenTurnSign: number;
  toScreenX(xM: number, yM: number): number;
  toScreenY(xM: number, yM: number): number;
  /** Screen → table. UI-001 aims from a touch point, so this is not optional. */
  toTableX(px: number, py: number): number;
  toTableY(px: number, py: number): number;
  /** A length in metres, in CSS pixels. */
  lengthPx(metres: number): number;
  /** A table-space direction as a screen-space one. Not a position — no origin. */
  screenDelta(dxM: number, dyM: number): ScreenVector;
  /** The playing surface's box on screen, so nothing has to re-derive it. */
  tableRect(): TableRect;
  /**
   * A table-space direction as the bearing the player reads: degrees 0..360
   * clockwise from straight up the screen.
   */
  bearingDegrees(dxM: number, dyM: number): number;
}

export function createViewport(
  spec: TableSpec,
  cssWidth: number,
  cssHeight: number,
  devicePixelRatio: number,
  marginPx = 0,
  /**
   * Width of the rails around the cloth, in metres (`frame.ts`). The fit makes room
   * for the whole frame, not just the cloth — fitting the cloth alone left the rails
   * and pocket rings to a fixed pixel margin, and cut them off (CLAUDE.md §13/L36).
   */
  frameM = 0,
  options: ViewportOptions = {},
): Viewport {
  const availableW = Math.max(1, cssWidth - marginPx * 2);
  const availableH = Math.max(1, cssHeight - marginPx * 2);
  const outerLongM = spec.widthM + frameM * 2;
  const outerShortM = spec.heightM + frameM * 2;

  /*
   * One uniform scale per orientation — the smaller fit wins, so the table always
   * sits inside the box. Then take whichever orientation draws it larger, which gives
   * portrait on a phone and landscape on a desktop without either being hard-coded.
   */
  const portraitScale = Math.min(availableW / outerShortM, availableH / outerLongM);
  const landscapeScale = Math.min(availableW / outerLongM, availableH / outerShortM);
  const preference = options.orientation ?? 'auto';
  const orientation: Orientation =
    preference !== 'auto' ? preference : landscapeScale > portraitScale ? 'landscape' : 'portrait';
  const fill = Math.min(1, Math.max(0.1, options.fill ?? 1));
  const scale = (orientation === 'landscape' ? landscapeScale : portraitScale) * fill;

  const drawnW = (orientation === 'landscape' ? spec.widthM : spec.heightM) * scale;
  const drawnH = (orientation === 'landscape' ? spec.heightM : spec.widthM) * scale;
  const left = (cssWidth - drawnW) / 2;
  const top = (cssHeight - drawnH) / 2;

  const toScreenX =
    orientation === 'landscape'
      ? (xM: number, _yM: number) => left + xM * scale
      : (_xM: number, yM: number) => left + yM * scale;

  const toScreenY =
    orientation === 'landscape'
      ? (_xM: number, yM: number) => top + drawnH - yM * scale
      : (xM: number, _yM: number) => top + drawnH - xM * scale;

  const toTableX =
    orientation === 'landscape'
      ? (px: number, _py: number) => (px - left) / scale
      : (_px: number, py: number) => (top + drawnH - py) / scale;

  const toTableY =
    orientation === 'landscape'
      ? (_px: number, py: number) => (top + drawnH - py) / scale
      : (px: number, _py: number) => (px - left) / scale;

  const originX = toScreenX(0, 0);
  const originY = toScreenY(0, 0);

  const screenDelta = (dxM: number, dyM: number): ScreenVector => ({
    x: toScreenX(dxM, dyM) - originX,
    y: toScreenY(dxM, dyM) - originY,
  });

  /*
   * Where table +x and table +y point on screen. Their cross product says whether the
   * mapping preserves handedness. Screen y grows DOWNWARD, so a positive cross product
   * means a counter-clockwise turn in table space reads clockwise on the cloth.
   */
  const alongX = screenDelta(1, 0);
  const alongY = screenDelta(0, 1);
  const cross = alongX.x * alongY.y - alongX.y * alongY.x;

  return {
    cssWidth,
    cssHeight,
    dpr: devicePixelRatio,
    scale,
    orientation,
    originX,
    originY,
    screenTurnSign: cross > 0 ? -1 : 1,
    toScreenX,
    toScreenY,
    toTableX,
    toTableY,
    lengthPx: (metres: number) => metres * scale,
    screenDelta,
    tableRect: () => ({ left, top, width: drawnW, height: drawnH }),

    bearingDegrees(dxM, dyM) {
      const { x, y } = screenDelta(dxM, dyM);
      // Straight up the screen is −y, hence the negation; atan2(right, up) is a
      // clockwise bearing from up, which is what a player reads off the guide line.
      const degrees = Math.atan2(x, -y) * (180 / Math.PI);
      return degrees < 0 ? degrees + 360 : degrees;
    },
  };
}
